/**
 * Turns backend errors into something a human can act on.
 *
 * Two reasons this exists rather than rendering `error.message`:
 *
 *  1. Raw Firestore and Functions errors leak internals — collection paths,
 *     console URLs with encoded index definitions, stack fragments. That is
 *     noise to the reader and detail we should not be putting on screen.
 *  2. Our own HttpsError messages ARE written for the user (they explain why a
 *     PDF was rejected, for instance), so those should pass through intact.
 */

interface CodedError { code?: string; message?: string }

const FRIENDLY: Record<string, string> = {
  'failed-precondition': 'That action is not allowed in the envelope’s current state.',
  'permission-denied': 'You do not have access to that.',
  unauthenticated: 'Your session expired. Sign in again.',
  'not-found': 'That item no longer exists.',
  'resource-exhausted': 'Too many requests. Wait a moment and try again.',
  unavailable: 'Cannot reach the server. Check your connection and try again.',
  internal: 'Something went wrong on our end. Try again.',
};

export function friendlyError(err: unknown, fallback = 'Something went wrong.'): string {
  const e = err as CodedError;
  const raw = e?.message ?? '';
  const code = (e?.code ?? '').replace(/^functions\//, '');

  // A missing composite index is our bug, not the user's. Never show them the
  // console URL; surface it where a developer will actually see it.
  if (/requires an index/i.test(raw)) {
    console.error('[beckersign] missing Firestore index — add it to firestore.indexes.json:', raw);
    return 'This view is still being prepared. Try again in a minute.';
  }

  if (FRIENDLY[code]) return FRIENDLY[code]!;

  // Our own validation messages are user-facing and end in a full stop; pass
  // them through. Anything long or URL-bearing is internal noise.
  if (raw && raw.length < 200 && !/https?:\/\//.test(raw)) return raw;

  if (raw) console.error('[beckersign]', raw);
  return fallback;
}
